import React, { useMemo } from 'react';
import type { CallStatistic } from 'types/call.type';
import type { CallAnalyticsMixFilter } from './callAnalyticsMix.types';
import {
    buildDonutSegments,
    buildStatusMixCards,
    formatAnalyticsCount,
    normalizeCallStatistic,
    type StatusMixCardData,
} from './callAnalyticsMix.utils';
import { CallStatusDonutChart } from './CallStatusDonutChart';

import styles from './CallAnalyticsDeepDive.module.scss';

export interface CallAnalyticsDeepDiveProps {
    callStats?: CallStatistic;
    humanTransfer?: boolean;
    activeFilter?: CallAnalyticsMixFilter | null;
    onFilterChange?: (filter: CallAnalyticsMixFilter | null) => void;
}

const formatCount = formatAnalyticsCount;

type StatusMixCardProps = {
    card: StatusMixCardData;
    isActive: boolean;
    isDimmed: boolean;
    onToggle: () => void;
};

const StatusMixCard: React.FC<StatusMixCardProps> = ({ card, isActive, isDimmed, onToggle }) => (
    <button
        type='button'
        className={[styles.statusCard, isActive ? styles.statusCardActive : '', isDimmed ? styles.dimmed : '']
            .filter(Boolean)
            .join(' ')}
        style={
            {
                '--status-color': card.color,
            } as React.CSSProperties
        }
        onClick={onToggle}
        data-testid={`status-mix-card-${card.id}`}
        aria-pressed={isActive}
    >
        <span className={styles.statusCardAccent} aria-hidden='true' />
        <div className={styles.statusCardHeader}>
            <span className={styles.statusCardLabel}>{card.label}</span>
            <span className={styles.statusCardTotal}>{formatCount(card.total)}</span>
        </div>
        <ul className={styles.subStatusList}>
            {card.subRows.map((row) => (
                <li key={row.label} className={styles.subStatusRow}>
                    <span className={(row.count ?? 0) === 0 ? styles.subStatusLabelMuted : styles.subStatusLabel}>
                        {row.label}
                    </span>
                    <span className={(row.count ?? 0) === 0 ? styles.subStatusCountMuted : styles.subStatusCount}>
                        {formatCount(row.count)}
                    </span>
                </li>
            ))}
        </ul>
    </button>
);

export const CallAnalyticsDeepDive: React.FC<CallAnalyticsDeepDiveProps> = ({
    callStats,
    humanTransfer = true,
    activeFilter = null,
    onFilterChange,
}) => {
    const statusCards = useMemo(() => buildStatusMixCards(callStats, humanTransfer), [callStats, humanTransfer]);
    const donutSegments = useMemo(() => buildDonutSegments(statusCards), [statusCards]);

    const grandTotal = normalizeCallStatistic(callStats).total;
    const activeStatusId = activeFilter?.type === 'status' ? activeFilter.key : null;

    const handleStatusToggle = (card: StatusMixCardData) => {
        if (!onFilterChange || card.total <= 0) return;

        const isSame = activeFilter?.type === 'status' && activeFilter.key === card.id;
        onFilterChange(isSame ? null : { type: 'status', key: card.id, label: card.label });
    };

    if (statusCards.length === 0) {
        return null;
    }

    return (
        <section className={styles.section} data-testid='call-analytics-deep-dive' aria-label='Call status mix'>
            <div className={styles.block}>
                <header className={styles.blockHeader}>
                    <h4>Call status mix</h4>
                    <p className={styles.blockSubtitle}>Click a status to filter calls below</p>
                </header>
                <div className={styles.statusMixBody}>
                    <div className={activeFilter?.type === 'disposition' ? styles.chartDimmed : ''}>
                        <CallStatusDonutChart
                            segments={donutSegments}
                            total={grandTotal}
                            activeSegmentId={activeStatusId}
                            onSegmentClick={(segmentId) => {
                                const card = statusCards.find((item) => item.id === segmentId);
                                if (card) handleStatusToggle(card);
                            }}
                        />
                    </div>
                    <div className={styles.statusCardGrid}>
                        {statusCards.map((card) => (
                            <StatusMixCard
                                key={card.id}
                                card={card}
                                isActive={activeStatusId === card.id}
                                isDimmed={
                                    activeFilter?.type === 'disposition' ||
                                    (activeFilter?.type === 'status' && activeStatusId !== card.id)
                                }
                                onToggle={() => handleStatusToggle(card)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
